import React, { useState } from "react";
import { 
  CheckCircle2, Copy, Check, Sparkles, Clock, Calendar, MessageSquare, 
  ChevronDown, ChevronUp, MapPin, Briefcase, Building2, Phone, Mail, 
  Send, AlertCircle, ArrowRight, XCircle, FileText, UserCheck, HelpCircle, 
  Filter, Sparkle
} from "lucide-react";
import { useSeoMetadata } from "../PublicPages";
import { SeoBreadcrumbs } from "./SeoBreadcrumbs";
import { SeoRelatedLinks } from "./SeoRelatedLinks";

interface Props {
  onNavigate: (path: string) => void;
  onLogin?: (isSignUp?: boolean) => void;
}

export function HowToAskIfABusinessIsHiringGuide({ onNavigate }: Props) {
  const currentRoute = "/guides/how-to-ask-if-a-business-is-hiring";
  const canonicalUrl = `https://getbiznearyou.com${currentRoute}`;

  // SEO metadata & Structured Data
  useSeoMetadata({
    title: "How to Ask if a Business Is Hiring (+ Scripts) | biznearyou",
    description: "Learn exactly how to ask a business if they’re hiring, including what to say in person, over the phone, by email, and what to do if they aren’t hiring yet.",
    canonicalUrl,
    schemaJson: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "How to Ask if a Business Is Hiring",
      "description": "Learn exactly how to ask a business if they’re hiring, including what to say in person, over the phone, by email, and what to do if they aren’t hiring yet.",
      "datePublished": "2026-01-15",
      "dateModified": "2026-08-08",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": canonicalUrl
      },
      "publisher": {
        "@type": "Organization",
        "name": "biznearyou",
        "logo": {
          "@type": "ImageObject",
          "url": "https://getbiznearyou.com/favicon-512.png"
        }
      }
    }
  });

  // State for copy buttons
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  // State for interactive script builder
  const [scriptChannel, setScriptChannel] = useState<"in-person" | "phone" | "email" | "text">("in-person");
  const [scriptJobType, setScriptJobType] = useState<"part-time" | "full-time" | "first-job" | "internship" | "general">("part-time");
  const [scriptBizType, setScriptBizType] = useState<string>("retail");

  // State for FAQ Accordion
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  // Generate customized script for builder
  const getCustomScript = () => {
    const jobLabel = scriptJobType === "part-time" ? "part-time" 
      : scriptJobType === "full-time" ? "full-time" 
      : scriptJobType === "first-job" ? "entry-level" 
      : scriptJobType === "internship" ? "internship" : "general";

    const bizLabel = scriptBizType === "retail" ? "retail"
      : scriptBizType === "restaurant" ? "restaurant / dining"
      : scriptBizType === "office" ? "office / administrative"
      : scriptBizType === "medical" ? "medical / dental practice"
      : scriptBizType === "trade" ? "trade / service"
      : "local business";

    if (scriptChannel === "in-person") {
      return `Hi! My name is [Name]. I'm looking for ${jobLabel} opportunities in ${bizLabel} and I really like what you do here. Are you currently hiring or taking applications for new team members?`;
    }
    if (scriptChannel === "phone") {
      return `Hi, my name is [Name]. I'm calling to ask if your team is currently hiring or accepting applications for ${jobLabel} positions in ${bizLabel}. Should I speak with a manager or stop by in person?`;
    }
    if (scriptChannel === "email") {
      return `Subject: Employment Inquiry – [Your Name] (${jobLabel.toUpperCase()})\n\nHello [Manager/Owner Name],\n\nMy name is [Name], and I am writing to ask whether [Business Name] is currently accepting applications for ${jobLabel} positions. I am very interested in your work in ${bizLabel} and bring strong reliability and communication skills.\n\nI would be happy to attach my resume or drop by in person if you have a moment. Thank you for your time!\n\nBest regards,\n[Your Name] | [Your Phone Number]`;
    }
    return `Hi! I was wondering if you’re currently hiring or accepting applications for ${jobLabel} positions at [Business Name]. I'm looking for work in ${bizLabel} and would love to join your team. Thanks!`;
  };

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="w-full bg-slate-50 dark:bg-slate-950 text-slate-800 dark:text-slate-100 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Breadcrumb Navigation */}
        <SeoBreadcrumbs
          items={[
            { label: "Guides", path: "/guides" },
            { label: "How to Ask if a Business Is Hiring", path: currentRoute }
          ]}
          onNavigate={onNavigate}
        />

        {/* Article Header */}
        <header className="my-6">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-blue-100 dark:bg-blue-950/70 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-900">
              Direct Outreach
            </span>
            <span className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              <span>12 min read</span>
            </span>
            <span className="text-xs text-slate-400">•</span>
            <span className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              <span>Updated Aug 8, 2026</span>
            </span>
          </div>

          <h1 className="text-3xl sm:text-5xl font-extrabold text-slate-900 dark:text-white tracking-tight leading-tight mb-4">
            How to Ask if a Business Is Hiring
          </h1>
          <p className="text-base sm:text-lg text-slate-600 dark:text-slate-300 leading-relaxed font-medium">
            Learn exactly what to say in person, over the phone, by email, and what to do if they aren’t hiring yet.
          </p>
        </header>

        {/* Direct Answer Box (Immediate Answer for Users & Search Engines) */}
        <section className="bg-gradient-to-br from-blue-50 to-indigo-50/50 dark:from-slate-900 dark:to-blue-950/40 border border-blue-200 dark:border-blue-900 rounded-2xl p-6 sm:p-8 mb-8 shadow-xs">
          <div className="flex items-start gap-3 mb-3">
            <Sparkles className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="text-xs font-bold text-blue-800 dark:text-blue-300 uppercase tracking-wider mb-1">
                Direct Answer
              </h2>
              <p className="text-base sm:text-lg font-semibold text-slate-900 dark:text-white leading-relaxed">
                The easiest way to ask if a business is hiring is to politely introduce yourself, explain that you’re interested in working there, and ask whether they’re currently accepting applications.
              </p>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800/80 rounded-xl p-4 sm:p-5 border border-blue-100 dark:border-slate-700 my-4">
            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
              Short Example Script:
            </p>
            <p className="text-sm font-medium text-slate-800 dark:text-slate-200 italic">
              “Hi, my name is [Name]. I’m interested in working here and wanted to ask whether you’re currently hiring or accepting applications.”
            </p>
          </div>

          <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            <strong>What if they aren’t hiring?</strong> Simply ask whether you can leave a resume or whether there is a good time to check back in the future.
          </p>
        </section>

        {/* Featured Snippet Optimization (7 Steps Quick List) */}
        <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 mb-8 shadow-xs">
          <h2 className="text-base font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-4 flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            <span>How to Ask if a Business Is Hiring in 7 Quick Steps</span>
          </h2>
          <ol className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs sm:text-sm text-slate-700 dark:text-slate-300">
            {[
              "1. Research the business and know what they do.",
              "2. Choose a good, off-peak time to contact them.",
              "3. Introduce yourself warmly and politely.",
              "4. State clearly that you're interested in working there.",
              "5. Ask whether they are currently hiring or accepting applications.",
              "6. Inquire about their preferred application process.",
              "7. Thank them sincerely and follow up appropriately."
            ].map((step, idx) => (
              <li key={idx} className="flex items-start gap-2 bg-slate-50 dark:bg-slate-800/50 p-3 rounded-xl border border-slate-100 dark:border-slate-800">
                <span className="font-semibold text-blue-600 dark:text-blue-400">{step}</span>
              </li>
            ))}
          </ol>
        </section>

        {/* Quick-Answer / Script Box Component */}
        <section className="bg-slate-900 text-white rounded-2xl p-6 sm:p-8 mb-8 shadow-md">
          <div className="flex items-center justify-between gap-2 mb-4">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-400" />
              <h2 className="text-base sm:text-lg font-bold">Quick Answer Scripts</h2>
            </div>
            <button
              onClick={() => handleCopy("Hi, my name is [Name]. I was wondering if you’re currently hiring or accepting applications. I’d be interested in working here.", "quick-script")}
              className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-semibold text-xs transition flex items-center gap-1.5 cursor-pointer"
            >
              {copiedKey === "quick-script" ? <Check className="w-3.5 h-3.5 text-emerald-300" /> : <Copy className="w-3.5 h-3.5" />}
              <span>{copiedKey === "quick-script" ? "Copied!" : "Copy Main Script"}</span>
            </button>
          </div>

          <div className="space-y-4">
            <div className="bg-slate-800/90 rounded-xl p-4 border border-slate-700">
              <span className="text-[11px] font-bold uppercase tracking-wider text-blue-300 block mb-1">What to Say:</span>
              <p className="text-sm text-slate-200 font-mono leading-relaxed">
                “Hi, my name is [Name]. I was wondering if you’re currently hiring or accepting applications. I’d be interested in working here.”
              </p>
            </div>

            <div className="bg-slate-800/90 rounded-xl p-4 border border-slate-700">
              <span className="text-[11px] font-bold uppercase tracking-wider text-amber-300 block mb-1">If They Say No:</span>
              <p className="text-sm text-slate-200 font-mono leading-relaxed">
                “No problem. Would it be okay if I left my resume, or is there a better time for me to check back?”
              </p>
            </div>
          </div>
        </section>

        {/* Interactive Table of Contents */}
        <nav className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 mb-8 shadow-xs">
          <h2 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-3 flex items-center gap-2">
            <Filter className="w-4 h-4 text-blue-600" />
            <span>Table of Contents</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs font-medium text-slate-600 dark:text-slate-300">
            {[
              { id: "best-way", label: "1. The Best Way to Ask" },
              { id: "script-builder", label: "2. Build Your Custom Script (Interactive)" },
              { id: "in-person", label: "3. What to Say in Person" },
              { id: "phone", label: "4. What to Say over the Phone" },
              { id: "email", label: "5. How to Ask by Email" },
              { id: "text-msg", label: "6. Texting or Social Messaging" },
              { id: "no-job-ad", label: "7. Asking When No Job Is Advertised" },
              { id: "who-to-ask", label: "8. Who You Should Ask For" },
              { id: "best-time", label: "9. Best Time to Visit" },
              { id: "what-to-bring", label: "10. What to Bring" },
              { id: "apply-online", label: "11. What If They Say 'Apply Online'?" },
              { id: "not-hiring", label: "12. What to Say If They Say No" },
              { id: "follow-up", label: "13. How to Follow Up" },
              { id: "biz-types", label: "14. What to Say by Business Type" },
              { id: "first-job", label: "15. What If This Is Your First Job?" },
              { id: "teenagers", label: "16. Asking as a Teenager" },
              { id: "resume-need", label: "17. Do You Need a Resume?" },
              { id: "mistakes", label: "18. Common Mistakes to Avoid" },
              { id: "decision-guide", label: "19. Visit, Call, or Email? Decision Matrix" },
              { id: "do-dont", label: "20. DO vs DON'T Guide" },
              { id: "faqs", label: "21. Frequently Asked Questions" }
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="text-left py-1.5 px-2.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-blue-600 dark:hover:text-blue-400 transition truncate cursor-pointer"
              >
                {item.label}
              </button>
            ))}
          </div>
        </nav>

        {/* Section 1: The Best Way to Ask */}
        <section id="best-way" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 mb-8 shadow-xs space-y-4">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
            1. The Best Way to Ask if a Business Is Hiring
          </h2>
          <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            The most effective way to ask whether a company is hiring is to combine courtesy with clarity. Business owners and managers appreciate directness when it is delivered politely and at an appropriate time.
          </p>
          <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            Instead of asking a vague question like <em>“Got any work?”</em> or launching into a lengthy speech, state who you are, mention your interest in their specific business, and ask if they are accepting applications.
          </p>
          <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900 text-xs text-blue-900 dark:text-blue-200">
            <strong>Key Strategy:</strong> Always phrase your question around whether they are <em>“accepting applications”</em> or <em>“preparing for upcoming openings.”</em> This allows employers to keep your information even if they don’t have an immediate start date today.
          </div>
        </section>

        {/* Section 2: Interactive Script Builder */}
        <section id="script-builder" className="bg-gradient-to-br from-indigo-950 to-slate-900 text-white rounded-2xl p-6 sm:p-8 mb-8 shadow-md">
          <div className="flex items-center gap-2 mb-2">
            <Sparkle className="w-5 h-5 text-indigo-400" />
            <h2 className="text-lg sm:text-xl font-bold">Build Your Hiring Question (Interactive Script Builder)</h2>
          </div>
          <p className="text-xs text-indigo-200 mb-6">
            Customize your outreach question based on your communication channel, job type, and target business industry.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 text-xs">
            {/* Channel */}
            <div>
              <label className="block font-bold text-slate-300 mb-2">Contact Method:</label>
              <div className="space-y-1.5">
                {[
                  { id: "in-person", label: "In Person" },
                  { id: "phone", label: "Phone Call" },
                  { id: "email", label: "Email" },
                  { id: "text", label: "Text / Social DM" }
                ].map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => setScriptChannel(opt.id as any)}
                    className={`w-full text-left px-3 py-2 rounded-xl font-semibold transition cursor-pointer border ${
                      scriptChannel === opt.id 
                        ? "bg-blue-600 text-white border-blue-500 shadow-xs" 
                        : "bg-slate-800/80 text-slate-300 border-slate-700 hover:bg-slate-800"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Job Type */}
            <div>
              <label className="block font-bold text-slate-300 mb-2">Job Type:</label>
              <div className="space-y-1.5">
                {[
                  { id: "part-time", label: "Part-Time Work" },
                  { id: "full-time", label: "Full-Time Work" },
                  { id: "first-job", label: "First Job / Entry-Level" },
                  { id: "internship", label: "Internship" },
                  { id: "general", label: "General Work" }
                ].map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => setScriptJobType(opt.id as any)}
                    className={`w-full text-left px-3 py-2 rounded-xl font-semibold transition cursor-pointer border ${
                      scriptJobType === opt.id 
                        ? "bg-blue-600 text-white border-blue-500 shadow-xs" 
                        : "bg-slate-800/80 text-slate-300 border-slate-700 hover:bg-slate-800"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Business Type */}
            <div>
              <label className="block font-bold text-slate-300 mb-2">Business Industry:</label>
              <div className="space-y-1.5">
                {[
                  { id: "retail", label: "Retail Store" },
                  { id: "restaurant", label: "Restaurant / Cafe" },
                  { id: "small-biz", label: "Small Local Business" },
                  { id: "office", label: "Office / Admin" },
                  { id: "trade", label: "Trade / Construction" },
                  { id: "medical", label: "Medical / Dental" }
                ].map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => setScriptBizType(opt.id)}
                    className={`w-full text-left px-3 py-2 rounded-xl font-semibold transition cursor-pointer border ${
                      scriptBizType === opt.id 
                        ? "bg-blue-600 text-white border-blue-500 shadow-xs" 
                        : "bg-slate-800/80 text-slate-300 border-slate-700 hover:bg-slate-800"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Generated Script Box */}
          <div className="bg-slate-900 border border-slate-700 p-5 rounded-2xl relative">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-bold text-indigo-400 uppercase tracking-wider">Your Custom Script:</span>
              <button
                onClick={() => handleCopy(getCustomScript(), "custom-script-builder")}
                className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-xs transition flex items-center gap-1.5 cursor-pointer"
              >
                {copiedKey === "custom-script-builder" ? <Check className="w-3.5 h-3.5 text-emerald-300" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copiedKey === "custom-script-builder" ? "Copied!" : "Copy Script"}</span>
              </button>
            </div>
            <pre className="text-xs sm:text-sm text-slate-200 font-mono whitespace-pre-wrap leading-relaxed">
              {getCustomScript()}
            </pre>
          </div>
        </section>

        {/* Section 3: How to Ask in Person */}
        <section id="in-person" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 mb-8 shadow-xs space-y-4">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
            3. How to Ask if a Business Is Hiring in Person
          </h2>
          <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            Walking into a business is often the fastest way to get hired at local retail stores, cafes, restaurants, gyms, and trade shops.
          </p>
          
          <div className="bg-slate-50 dark:bg-slate-800/60 p-5 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-2">
            <h3 className="text-sm font-bold text-slate-900 dark:text-white">Step-by-Step Walk-In Process:</h3>
            <ul className="space-y-1.5 text-xs sm:text-sm text-slate-700 dark:text-slate-300">
              <li>• <strong>Choose a quiet time:</strong> Avoid lunch or dinner rushes for food service, and busy morning openings for trade offices.</li>
              <li>• <strong>Dress neatly:</strong> Clean, presentable attire appropriate for the type of workplace.</li>
              <li>• <strong>Bring printed resumes:</strong> Carry 2–3 neat copies in a clean folder or envelope.</li>
              <li>• <strong>Ask for the manager:</strong> Politely ask frontline staff if the hiring manager is available for a moment.</li>
              <li>• <strong>Keep it short:</strong> Respect their time—state your name, interest, and ask about applications.</li>
            </ul>
          </div>

          {/* In-Person Scripts */}
          <div className="space-y-3 pt-2">
            <div className="bg-slate-50 dark:bg-slate-800/80 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs font-bold text-blue-600 dark:text-blue-400">Simple Version:</span>
                <button onClick={() => handleCopy("Hi, I was wondering if you’re currently hiring or accepting applications?", "script-inperson-simple")} className="text-xs text-slate-500 hover:text-blue-600 flex items-center gap-1 cursor-pointer">
                  <Copy className="w-3 h-3" /> Copy
                </button>
              </div>
              <p className="text-xs sm:text-sm text-slate-800 dark:text-slate-200 font-mono">“Hi, I was wondering if you’re currently hiring or accepting applications?”</p>
            </div>

            <div className="bg-slate-50 dark:bg-slate-800/80 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs font-bold text-blue-600 dark:text-blue-400">Better Version:</span>
                <button onClick={() => handleCopy("Hi, my name is [Name]. I’m looking for [part-time/full-time] work and I’m really interested in working here. Are you currently hiring or accepting applications?", "script-inperson-better")} className="text-xs text-slate-500 hover:text-blue-600 flex items-center gap-1 cursor-pointer">
                  <Copy className="w-3 h-3" /> Copy
                </button>
              </div>
              <p className="text-xs sm:text-sm text-slate-800 dark:text-slate-200 font-mono">“Hi, my name is [Name]. I’m looking for [part-time/full-time] work and I’m really interested in working here. Are you currently hiring or accepting applications?”</p>
            </div>

            <div className="bg-slate-50 dark:bg-slate-800/80 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs font-bold text-blue-600 dark:text-blue-400">Directly to a Manager:</span>
                <button onClick={() => handleCopy("Hi, I’m [Name]. I’m interested in working with your team and wanted to introduce myself. Are you currently looking for anyone, or would it be okay if I left my resume for future openings?", "script-inperson-mgr")} className="text-xs text-slate-500 hover:text-blue-600 flex items-center gap-1 cursor-pointer">
                  <Copy className="w-3 h-3" /> Copy
                </button>
              </div>
              <p className="text-xs sm:text-sm text-slate-800 dark:text-slate-200 font-mono">“Hi, I’m [Name]. I’m interested in working with your team and wanted to introduce myself. Are you currently looking for anyone, or would it be okay if I left my resume for future openings?”</p>
            </div>
          </div>
        </section>

        {/* Section 4: How to Call and Ask over the Phone */}
        <section id="phone" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 mb-8 shadow-xs space-y-4">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
            4. How to Call and Ask if a Business Is Hiring
          </h2>
          <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            Calling ahead saves travel time and helps you verify whether a business takes walk-ins or requires an online application before you visit.
          </p>

          <div className="bg-slate-50 dark:bg-slate-800/60 p-4 rounded-xl border border-slate-200 dark:border-slate-700 space-y-1.5 text-xs sm:text-sm">
            <p className="font-bold text-slate-900 dark:text-white">Before Calling Checklist:</p>
            <ul className="space-y-1 text-slate-700 dark:text-slate-300">
              <li>✓ Check their official website first to see if a career page exists.</li>
              <li>✓ Know the exact business name and type of position you want.</li>
              <li>✓ Have a pen and notepad ready to write down manager names or links.</li>
              <li>✓ Avoid calling during peak customer hours.</li>
            </ul>
          </div>

          <div className="bg-slate-50 dark:bg-slate-800/80 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs font-bold text-blue-600 dark:text-blue-400">Phone Script:</span>
              <button onClick={() => handleCopy("Hi, my name is [Name]. I’m calling because I’m interested in working with your business. I wanted to ask whether you’re currently hiring or accepting applications.", "script-phone-std")} className="text-xs text-slate-500 hover:text-blue-600 flex items-center gap-1 cursor-pointer">
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
            <p className="text-xs sm:text-sm text-slate-800 dark:text-slate-200 font-mono">“Hi, my name is [Name]. I’m calling because I’m interested in working with your business. I wanted to ask whether you’re currently hiring or accepting applications.”</p>
          </div>

          <div className="bg-slate-50 dark:bg-slate-800/80 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs font-bold text-blue-600 dark:text-blue-400">If Speaking to General Reception Staff:</span>
              <button onClick={() => handleCopy("Could I please ask who I should speak to about employment opportunities?", "script-phone-staff")} className="text-xs text-slate-500 hover:text-blue-600 flex items-center gap-1 cursor-pointer">
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
            <p className="text-xs sm:text-sm text-slate-800 dark:text-slate-200 font-mono">“Could I please ask who I should speak to about employment opportunities?”</p>
          </div>
        </section>

        {/* Section 5: How to Email a Business */}
        <section id="email" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 mb-8 shadow-xs space-y-4">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
            5. How to Email a Business Asking if They’re Hiring
          </h2>
          <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            Email works best for offices, medical practices, marketing agencies, and local companies where managers are busy throughout the day and accept digital resumes.
          </p>

          <div className="bg-slate-900 text-slate-100 p-5 rounded-2xl border border-slate-800 space-y-3 font-mono text-xs">
            <div className="flex justify-between items-center border-b border-slate-800 pb-2">
              <span className="text-blue-400 font-bold">Email Template</span>
              <button onClick={() => handleCopy(`Subject: Employment Inquiry – [Your Name]\n\nHello [Manager/Business Name],\n\nMy name is [Name], and I’m interested in opportunities with [Business Name]. I wanted to ask whether you’re currently hiring or accepting applications for [type of work].\n\nI have experience with [brief relevant skill/experience], and I’d be happy to send through my resume if useful.\n\nThank you for your time,\n[Name]`, "script-email-template")} className="text-slate-400 hover:text-white flex items-center gap-1 cursor-pointer">
                <Copy className="w-3 h-3" /> Copy Template
              </button>
            </div>
            <p><strong>Subject:</strong> Employment Inquiry – [Your Name]</p>
            <p>Hello [Manager/Business Name],</p>
            <p>My name is [Name], and I’m interested in opportunities with [Business Name]. I wanted to ask whether you’re currently hiring or accepting applications for [type of work].</p>
            <p>I have experience with [brief relevant skill/experience], and I’d be happy to send through my resume if useful.</p>
            <p>Thank you for your time,<br />[Your Name]<br />[Your Phone Number]</p>
          </div>
        </section>

        {/* Section 6: Can You Message a Business (Text / Social) */}
        <section id="text-msg" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 mb-8 shadow-xs space-y-4">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
            6. Can You Message a Business to Ask if They’re Hiring?
          </h2>
          <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            Messaging via Instagram, Facebook, or SMS is appropriate primarily for very small, independent businesses like boutique shops, salons, or food trucks that actively communicate with customers via social media.
          </p>

          <div className="bg-slate-50 dark:bg-slate-800/80 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs font-bold text-blue-600 dark:text-blue-400">Social Media DM Script:</span>
              <button onClick={() => handleCopy("Hi! I was wondering if you’re currently hiring or accepting resumes. I’m looking for [part-time/full-time] work and would be interested in working with you. Thanks!", "script-dm")} className="text-xs text-slate-500 hover:text-blue-600 flex items-center gap-1 cursor-pointer">
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
            <p className="text-xs sm:text-sm text-slate-800 dark:text-slate-200 font-mono">“Hi! I was wondering if you’re currently hiring or accepting resumes. I’m looking for [part-time/full-time] work and would be interested in working with you. Thanks!”</p>
          </div>
        </section>

        {/* Section 7: Asking When No Job Is Advertised */}
        <section id="no-job-ad" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 mb-8 shadow-xs space-y-4">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
            7. How to Ask About a Job That Isn’t Advertised
          </h2>
          <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            Many small employers do not create public job postings due to time and advertising costs. You do not need to pretend a job exists—simply ask if they accept expressions of interest or hold resumes for upcoming growth.
          </p>

          <div className="bg-slate-50 dark:bg-slate-800/80 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs font-bold text-blue-600 dark:text-blue-400">Unadvertised Job Script:</span>
              <button onClick={() => handleCopy("I couldn’t see any positions advertised, but I’m interested in working here. Do you accept resumes or expressions of interest for future openings?", "script-no-ad")} className="text-xs text-slate-500 hover:text-blue-600 flex items-center gap-1 cursor-pointer">
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
            <p className="text-xs sm:text-sm text-slate-800 dark:text-slate-200 font-mono">“I couldn’t see any positions advertised, but I’m interested in working here. Do you accept resumes or expressions of interest for future openings?”</p>
          </div>
        </section>

        {/* Section 8: Who to Ask */}
        <section id="who-to-ask" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 mb-8 shadow-xs space-y-4">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
            8. Who Should You Ask if a Business Is Hiring?
          </h2>
          <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            Connecting with the right person ensures your inquiry gets answered promptly. Here is who to ask for based on business type:
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs sm:text-sm">
            {[
              { type: "Small Local Business", title: "Owner, Founder, or Manager" },
              { type: "Retail Store", title: "Store Manager or Assistant Manager" },
              { type: "Restaurant / Cafe", title: "General Manager, Shift Supervisor, or Head Chef" },
              { type: "Office / Corporate", title: "Office Manager, HR Coordinator, or Hiring Manager" },
              { type: "Trade / Construction", title: "Operations Manager, Field Supervisor, or Owner" },
              { type: "Medical / Dental", title: "Practice Manager or Office Administrator" }
            ].map((item, idx) => (
              <div key={idx} className="p-3.5 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700">
                <span className="font-bold text-blue-600 dark:text-blue-400 block mb-0.5">{item.type}</span>
                <span className="text-slate-700 dark:text-slate-300">Ask for: {item.title}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Section 9: Best Time to Ask */}
        <section id="best-time" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 mb-8 shadow-xs space-y-4">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
            9. When Is the Best Time to Ask?
          </h2>
          <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            Timing matters immensely. Arriving during peak customer hours makes it difficult for staff or managers to talk to you.
          </p>

          <div className="space-y-2 text-xs sm:text-sm text-slate-700 dark:text-slate-300">
            <p>• <strong>Restaurants & Cafes:</strong> Between 2:00 PM and 4:00 PM (after lunch and before dinner service).</p>
            <p>• <strong>Retail Stores:</strong> Weekday mornings between 10:00 AM and 11:30 AM.</p>
            <p>• <strong>Offices & Medical Practices:</strong> Mid-mornings or mid-afternoons on Tuesday through Thursday.</p>
            <p>• <strong>Trades & Field Services:</strong> Early morning between 7:30 AM and 8:30 AM before crews deploy.</p>
          </div>
        </section>

        {/* Section 10: What to Bring */}
        <section id="what-to-bring" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 mb-8 shadow-xs space-y-4">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
            10. What Should You Bring When Asking in Person?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs sm:text-sm">
            <div className="p-4 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700">
              <span className="font-bold text-slate-900 dark:text-white block mb-1">Essential Checklist:</span>
              <ul className="space-y-1 text-slate-700 dark:text-slate-300">
                <li>• 2–3 printed copies of your resume</li>
                <li>• Phone or notebook with written availability</li>
                <li>• List of 2 professional or personal references</li>
                <li>• Working pen for paper application forms</li>
              </ul>
            </div>
            <div className="p-4 bg-blue-50 dark:bg-blue-950/40 rounded-xl border border-blue-200 dark:border-blue-900 text-blue-900 dark:text-blue-200">
              <span className="font-bold block mb-1">Note on Online Portals:</span>
              <p>Some businesses exclusively use online application portals. Bringing a paper resume allows you to hand over your contact details even if they instruct you to complete the formal application online afterward.</p>
            </div>
          </div>
        </section>

        {/* Section 11: What If They Tell You to Apply Online? */}
        <section id="apply-online" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 mb-8 shadow-xs space-y-4">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
            11. What If They Tell You to Apply Online?
          </h2>
          <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            Being told to apply online is not a rejection—it is simply their official compliance and HR workflow. Treat it as positive guidance!
          </p>

          <div className="bg-slate-50 dark:bg-slate-800/80 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs font-bold text-blue-600 dark:text-blue-400">Response Script:</span>
              <button onClick={() => handleCopy("Absolutely, thanks. I’ll submit it online today. Is there a particular position or location I should select?", "script-apply-online")} className="text-xs text-slate-500 hover:text-blue-600 flex items-center gap-1 cursor-pointer">
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
            <p className="text-xs sm:text-sm text-slate-800 dark:text-slate-200 font-mono">“Absolutely, thanks. I’ll submit it online today. Is there a particular position or location I should select?”</p>
          </div>
        </section>

        {/* Section 12: What to Say if They Aren't Hiring */}
        <section id="not-hiring" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 mb-8 shadow-xs space-y-4">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
            12. What to Say if They Aren’t Hiring Right Now
          </h2>
          <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            If a business isn’t currently hiring, maintain a friendly and professional impression so they remember you when an opening arises later.
          </p>

          <div className="space-y-2">
            {[
              "“No worries, thanks for letting me know. Would it be okay if I left my resume in case something opens up?”",
              "“Thanks anyway! Is there a better time of year when you normally take applications?”",
              "“Would you mind if I checked back in a few weeks?”"
            ].map((scriptText, i) => (
              <div key={i} className="p-3.5 bg-slate-50 dark:bg-slate-800/80 rounded-xl border border-slate-200 dark:border-slate-700 flex items-center justify-between gap-2">
                <span className="text-xs sm:text-sm font-mono text-slate-800 dark:text-slate-200">{scriptText}</span>
                <button onClick={() => handleCopy(scriptText, `script-no-${i}`)} className="text-xs text-slate-500 hover:text-blue-600 flex items-center gap-1 cursor-pointer flex-shrink-0">
                  <Copy className="w-3 h-3" /> Copy
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Section 13: How to Follow Up */}
        <section id="follow-up" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 mb-8 shadow-xs space-y-4">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
            13. How to Follow Up After Asking About a Job
          </h2>
          <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            A single polite follow-up after 5 to 7 days shows genuine initiative. Avoid repeatedly contacting managers every few days.
          </p>

          <div className="bg-slate-50 dark:bg-slate-800/80 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
            <span className="text-xs font-bold text-blue-600 dark:text-blue-400 block mb-1">Sample Follow-Up Phone/Email Script:</span>
            <p className="text-xs sm:text-sm font-mono text-slate-800 dark:text-slate-200">
              “Hi [Manager Name], this is [Name]. I dropped off my resume last week regarding potential openings. I wanted to check in briefly to see if you needed any additional information.”
            </p>
          </div>
        </section>

        {/* Section 14: What to Say at Different Types of Businesses */}
        <section id="biz-types" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 mb-8 shadow-xs space-y-4">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
            14. What to Say at Different Types of Businesses
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
            {[
              { type: "Retail Store", script: "“Hi, I love your store and was wondering if you have any part-time cashier or floor associate openings?”" },
              { type: "Restaurant / Cafe", script: "“Hi, I'm interested in joining your front-of-house or kitchen team. Are you currently taking applications?”" },
              { type: "Grocery Store", script: "“Hi, are you accepting applications for stocking, cashiering, or department roles right now?”" },
              { type: "Small Local Shop", script: "“Hi, I live nearby and love what you do here. Are you looking for extra help or weekend assistance?”" },
              { type: "Office / Admin", script: "“Hello, I'm inquiring whether you have entry-level administrative or front-desk openings available.”" },
              { type: "Hotel / Hospitality", script: "“Hi, I wanted to ask if you have front desk or housekeeping positions accepting applications.”" },
              { type: "Gym / Fitness", script: "“Hi! I'm interested in working at the front desk or assisting members. Are you taking applications?”" },
              { type: "Trade / Construction", script: "“Hi, I'm looking for labor or apprentice work and wanted to ask if you're adding crew members.”" },
              { type: "Medical / Dental Office", script: "“Hello, I am reaching out to inquire about front office or receptionist opportunities with your practice.”" }
            ].map((card, i) => (
              <div key={i} className="p-3.5 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700 flex flex-col justify-between space-y-2">
                <div>
                  <span className="font-bold text-slate-900 dark:text-white block mb-1">{card.type}</span>
                  <p className="text-slate-600 dark:text-slate-300 italic">{card.script}</p>
                </div>
                <button onClick={() => handleCopy(card.script, `biz-card-${i}`)} className="text-blue-600 dark:text-blue-400 font-semibold text-[11px] hover:underline flex items-center gap-1 cursor-pointer">
                  <Copy className="w-3 h-3" /> Copy Script
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Section 15: What If This Is Your First Job? */}
        <section id="first-job" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 mb-8 shadow-xs space-y-4">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
            15. What If This Is Your First Job? (No Experience)
          </h2>
          <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            Employers hiring for entry-level jobs care far more about punctuality, enthusiasm, and willingness to learn than a long work history.
          </p>

          <div className="bg-slate-50 dark:bg-slate-800/80 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
            <span className="text-xs font-bold text-blue-600 dark:text-blue-400 block mb-1">First Job Script:</span>
            <p className="text-xs sm:text-sm font-mono text-slate-800 dark:text-slate-200">
              “Hi, I’m looking for my first part-time job and I’m interested in working here. I’m reliable, eager to learn, and available evenings and weekends. Are you currently hiring or accepting applications?”
            </p>
          </div>

          <div className="pt-2">
            <button
              onClick={() => onNavigate("/jobs/no-experience-jobs")}
              className="text-xs font-bold text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
            >
              <span>Browse No-Experience Job Categories</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </section>

        {/* Section 16: Asking as a Teenager */}
        <section id="teenagers" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 mb-8 shadow-xs space-y-4">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
            16. How to Ask if a Business Is Hiring as a Teenager
          </h2>
          <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            If you are 14–17 years old, be clear about your school schedule and availability. Check local work permit rules for your state where applicable.
          </p>
          <div className="pt-1">
            <button
              onClick={() => onNavigate("/jobs/teen-jobs")}
              className="px-4 py-2 rounded-xl bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 text-xs font-bold border border-blue-200 dark:border-blue-900 hover:bg-blue-100 transition inline-flex items-center gap-1.5"
            >
              <span>Explore Teen-Friendly Local Jobs</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </section>

        {/* Section 17: Do You Need a Resume? */}
        <section id="resume-need" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 mb-8 shadow-xs space-y-4">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
            17. Do You Need a Resume to Ask if They’re Hiring?
          </h2>
          <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            No, you do not strictly need a resume just to ask if a business is accepting applications. However, having a simple 1-page document with your name, contact info, school or work history, and availability helps small business owners contact you quickly.
          </p>
        </section>

        {/* Section 18: Common Mistakes to Avoid */}
        <section id="mistakes" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 mb-8 shadow-xs space-y-4">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <XCircle className="w-5 h-5 text-rose-600 dark:text-rose-400" />
            <span>18. Mistakes to Avoid</span>
          </h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs sm:text-sm text-slate-700 dark:text-slate-300">
            {[
              "Arriving during peak customer rush hours.",
              "Demanding to speak strictly with the owner immediately.",
              "Asking casually with phrases like 'You guys got jobs?'",
              "Knowing nothing about what the business actually sells or does.",
              "Being dismissive or rude to frontline staff.",
              "Staying too long when managers are busy.",
              "Repeatedly calling or visiting every couple of days.",
              "Exaggerating work experience on paper.",
              "Ignoring explicit instructions to complete an online form.",
              "Leaving inaccurate phone numbers or email addresses.",
              "Complaining about past managers or employers."
            ].map((item, idx) => (
              <li key={idx} className="p-2.5 bg-rose-50/50 dark:bg-rose-950/20 rounded-xl border border-rose-100 dark:border-rose-900/40 text-slate-800 dark:text-slate-200">
                ✖ {item}
              </li>
            ))}
          </ul>
        </section>

        {/* Section 19: Decision Guide Matrix */}
        <section id="decision-guide" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 mb-8 shadow-xs space-y-4">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
            19. Should You Visit, Call, or Email? (Decision Matrix)
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs sm:text-sm">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80">
                  <th className="p-3 font-bold">Contact Method</th>
                  <th className="p-3 font-bold">Best For</th>
                  <th className="p-3 font-bold">Key Advantage</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                <tr>
                  <td className="p-3 font-bold text-blue-600 dark:text-blue-400">In Person</td>
                  <td className="p-3">Retail, Restaurants, Cafes, Salons, Gyms</td>
                  <td className="p-3">Immediate personal impression & face-to-face rapport</td>
                </tr>
                <tr>
                  <td className="p-3 font-bold text-blue-600 dark:text-blue-400">Phone Call</td>
                  <td className="p-3">Trade contractors, Medical practices, Checking application process</td>
                  <td className="p-3">Fast screening before traveling</td>
                </tr>
                <tr>
                  <td className="p-3 font-bold text-blue-600 dark:text-blue-400">Email</td>
                  <td className="p-3">Offices, Corporate, Professional services, Agencies</td>
                  <td className="p-3">Allows detailed resume attachment & convenient manager review</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Section 20: DO vs DON'T Guide */}
        <section id="do-dont" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 mb-8 shadow-xs space-y-4">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
            20. DO vs DON'T Guide
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs sm:text-sm">
            <div className="p-4 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/60 rounded-2xl space-y-2">
              <span className="font-bold text-emerald-800 dark:text-emerald-300 text-sm block">DO:</span>
              <p>✔ “Hi, I’m interested in working here. Are you currently accepting applications?”</p>
              <p>✔ “I’d be happy to apply online. Which position should I look for?”</p>
              <p>✔ “Would it be okay if I left my resume in case an opening comes up later?”</p>
            </div>

            <div className="p-4 bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/60 rounded-2xl space-y-2">
              <span className="font-bold text-rose-800 dark:text-rose-300 text-sm block">DON'T:</span>
              <p>✖ “You guys got jobs?”</p>
              <p>✖ “Can you just give my resume to your boss directly?”</p>
              <p>✖ “How much does this job pay?” before knowing if an opening exists.</p>
            </div>
          </div>
        </section>

        {/* Natural biznearyou Product CTA */}
        <section className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-2xl p-6 sm:p-8 text-center my-10 shadow-md">
          <h2 className="text-xl sm:text-2xl font-extrabold mb-2">
            Find Businesses Worth Contacting
          </h2>
          <p className="text-xs sm:text-sm text-blue-100 max-w-2xl mx-auto mb-6 leading-relaxed">
            Now that you know what to say, discover nearby independent businesses and local employers in your area beyond the big national job boards.
          </p>
          <button
            onClick={() => onNavigate("/find-jobs")}
            className="px-6 py-3 rounded-xl bg-white text-blue-700 font-bold text-xs sm:text-sm hover:bg-blue-50 transition shadow-xs inline-flex items-center gap-2 cursor-pointer"
          >
            <span>Find Businesses Near You</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </section>

        {/* Section 21: Frequently Asked Questions (FAQ) */}
        <section id="faqs" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 mb-8 shadow-xs space-y-4">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2 mb-4">
            <HelpCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <span>21. Frequently Asked Questions</span>
          </h2>

          <div className="space-y-3">
            {[
              { q: "Is it okay to walk into a business and ask if they’re hiring?", a: "Yes! Walking into local retail shops, cafes, restaurants, and small businesses is a standard and respected way to ask about job openings, provided you visit during off-peak hours." },
              { q: "What should I say when asking for a job?", a: "Introduce yourself politely, state that you're interested in working there, and ask whether they are currently accepting applications." },
              { q: "Should I ask for the manager?", a: "Yes. If frontline staff aren't sure, politely ask if the hiring manager or general manager is available for a quick moment." },
              { q: "Can I call a business and ask if they’re hiring?", a: "Absolutely. Calling is a great way to confirm whether a business takes walk-in applications or prefers online submissions before you travel." },
              { q: "Is it better to ask in person or online?", a: "In-person visits work best for small local businesses, food service, and retail. Corporate offices and large healthcare chains usually require online applications." },
              { q: "Should I bring a resume when I ask?", a: "Yes. Bringing a clean paper resume ensures you can hand over your contact details if a manager is present." },
              { q: "What if they tell me to apply online?", a: "Thank them politely, ask which specific job title to select online, and submit your application promptly." },
              { q: "What if they aren’t hiring right now?", a: "Politely ask if you can leave a resume or whether there is a good time to check back in the future." },
              { q: "Can I ask about jobs that aren’t advertised?", a: "Yes! Simply ask if they accept expressions of interest or hold resumes for upcoming growth." },
              { q: "How do I ask if a business is hiring with no experience?", a: "Focus on your reliability, positive attitude, willingness to learn, and availability for entry-level work." },
              { q: "How do I ask if a store is hiring?", a: "Visit on a weekday morning, introduce yourself to a supervisor or store manager, and ask if they are accepting cashier or floor associate applications." },
              { q: "How do I ask a restaurant if they’re hiring?", a: "Visit between 2:00 PM and 4:00 PM between meal rushes, ask for the general manager, and express interest in front-of-house or kitchen roles." },
              { q: "Is it rude to ask if a company is hiring?", a: "Not at all! As long as you are polite, brief, and respect their time, employers view job inquiries as a sign of initiative." }
            ].map((faq, idx) => (
              <div key={idx} className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full text-left p-4 bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition flex items-center justify-between font-semibold text-xs sm:text-sm text-slate-900 dark:text-white cursor-pointer"
                >
                  <span>{faq.q}</span>
                  {openFaq === idx ? <ChevronUp className="w-4 h-4 text-blue-600 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />}
                </button>
                {openFaq === idx && (
                  <div className="p-4 bg-white dark:bg-slate-900 text-xs sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed border-t border-slate-200 dark:border-slate-800">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Related Job Category Cards */}
        <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 mb-8 shadow-xs">
          <h2 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-4">
            Browse Jobs by Category
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 text-xs font-semibold">
            {[
              { label: "Part-Time Jobs", path: "/jobs/part-time-jobs" },
              { label: "First Jobs", path: "/jobs/first-jobs" },
              { label: "Teen Jobs", path: "/jobs/teen-jobs" },
              { label: "No Experience Jobs", path: "/jobs/no-experience-jobs" },
              { label: "Restaurant Jobs", path: "/jobs/restaurant-jobs" },
              { label: "Retail Jobs", path: "/jobs/retail-jobs" },
              { label: "All Job Categories", path: "/jobs" },
              { label: "Local Employer Search", path: "/find-jobs" }
            ].map((cat, idx) => (
              <button
                key={idx}
                onClick={() => onNavigate(cat.path)}
                className="text-left p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 hover:bg-blue-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 border border-slate-200 dark:border-slate-700 transition truncate cursor-pointer"
              >
                {cat.label}
              </button>
            ))}
          </div>
        </section>

        {/* Related Guides Section */}
        <section className="mb-8">
          <SeoRelatedLinks currentRoute={currentRoute} onNavigate={onNavigate} />
        </section>

      </div>
    </div>
  );
}
